import { cn } from '@/lib/utils'
import { Check } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { ONBOARDING_STEPS_CONFIG, type OnboardingStepConfig } from './types'

type OnboardingStepperProps = {
  currentStep: number
  onStepClick?: (step: number) => void
  className?: string
  stepsConfig?: OnboardingStepConfig[]
}

export function OnboardingStepper({
  currentStep,
  onStepClick,
  className,
  stepsConfig = ONBOARDING_STEPS_CONFIG,
}: OnboardingStepperProps) {
  const { t } = useTranslation('onboarding')

  return (
    <div className={cn('w-full', className)}>
      {/* Mobile stepper - horizontal dots */}
      <div className="flex items-center justify-center gap-2 md:hidden mb-6">
        {stepsConfig.map((_, index) => (
          <button
            key={index}
            onClick={() => onStepClick?.(index)}
            disabled={!onStepClick}
            className={cn(
              'w-2.5 h-2.5 rounded-full transition-all duration-300',
              index === currentStep 
                ? 'bg-[#F08A24] w-8' 
                : index < currentStep 
                  ? 'bg-[#0B2A4A]'
                  : 'bg-gray-200 dark:bg-gray-700'
            )}
            aria-label={`Step ${index + 1}`}
          />
        ))}
      </div>

      {/* Desktop stepper - vertical with labels */}
      <div className="hidden md:flex flex-col gap-1">
        {stepsConfig.map((step, index) => {
          const isCompleted = index < currentStep
          const isCurrent = index === currentStep
          const isPending = index > currentStep

          return (
            <button
              key={step.id}
              onClick={() => onStepClick?.(index)}
              disabled={!onStepClick || isPending}
              className={cn(
                'group flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-left',
                isCurrent && 'bg-[#F08A24]/10 border border-[#F08A24]/30',
                isCompleted && 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                isPending && 'opacity-50 cursor-not-allowed'
              )}
            >
              {/* Step indicator */}
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all duration-300',
                  isCurrent && 'bg-[#F08A24] text-white shadow-md shadow-[#F08A24]/30',
                  isCompleted && 'bg-[#0B2A4A] text-white',
                  isPending && 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                )}
              >
                {isCompleted ? (
                  <Check weight="bold" className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>

              {/* Step info */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium truncate',
                    isCurrent && 'text-[#0B2A4A] dark:text-white',
                    isCompleted && 'text-gray-700 dark:text-gray-300',
                    isPending && 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {t(step.title)}
                </p>
                {isCurrent && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {t(step.description)}
                  </p>
                )}
              </div>

              {/* Optional badge */}
              {step.optional && isPending && (
                <span className="text-xs text-gray-400 dark:text-gray-500 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800">
                  {t('optional')}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Progress bar */}
      <div className="hidden md:block mt-6">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
          <span>{t('progress')}</span>
          <span>{Math.round((currentStep / (stepsConfig.length - 1)) * 100)}%</span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#0B2A4A] to-[#F08A24] rounded-full transition-all duration-500 ease-out"
            style={{ 
              width: `${(currentStep / (stepsConfig.length - 1)) * 100}%` 
            }}
          />
        </div>
      </div>
    </div>
  )
}
